import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { AssetClass, AssetClassData, FixedAssetData } from '@/types/api';

const KEY = ['fixed-assets'] as const;

export type AssetStatus = 'in_progress' | 'in_service' | 'disposed';

/**
 * Invalidate everything a capitalization touches. A cost posts a journal entry
 * and moves a bank transaction out of the review queue, so the ledger views go
 * stale alongside the register.
 */
function refreshCapital(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ['bank', 'transactions'] });
  qc.invalidateQueries({ queryKey: ['accounting'] });
}

export function useFixedAssets(status?: AssetStatus) {
  return useQuery({
    queryKey: [...KEY, status ?? 'all'],
    queryFn: async (): Promise<FixedAssetData[]> => {
      const { data } = await api.get<FixedAssetData[]>('/v1/accounting/fixed-assets', {
        params: status ? { status } : undefined,
      });
      return data;
    },
  });
}

/**
 * The classes, each carrying its own tax consequences. Served rather than
 * hardcoded so the recovery period and the §179 rule have exactly one home.
 */
export function useAssetClasses() {
  return useQuery({
    queryKey: [...KEY, 'classes'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<AssetClassData[]> => {
      const { data } = await api.get<AssetClassData[]>('/v1/accounting/asset-classes');
      return data;
    },
  });
}

export type NewAssetInput = {
  name: string;
  asset_class: AssetClass;
  description?: string | null;
  acquired_on?: string | null;
};

export function useCreateFixedAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewAssetInput): Promise<FixedAssetData> => {
      await primeCsrf();
      const { data } = await api.post<FixedAssetData>('/v1/accounting/fixed-assets', input);
      return data;
    },
    onSuccess: () => refreshCapital(qc),
  });
}

export function useUpdateFixedAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: { id: number } & Partial<NewAssetInput> & { notes?: string | null }): Promise<FixedAssetData> => {
      await primeCsrf();
      const { data } = await api.patch<FixedAssetData>(`/v1/accounting/fixed-assets/${id}`, patch);
      return data;
    },
    onSuccess: () => refreshCapital(qc),
  });
}

export type PlaceInServiceInput = {
  placed_in_service_on: string;
  business_use_pct?: number;
  section_179_cents?: number;
  bonus_pct?: number;
};

/**
 * Freeze the basis and record the year-one election. The date is the day the
 * asset was ready for use, which sets the tax year the whole deduction lands
 * in, so it is asked for rather than inferred from the last invoice.
 */
export function usePlaceInService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: { id: number } & PlaceInServiceInput): Promise<FixedAssetData> => {
      await primeCsrf();
      const { data } = await api.post<FixedAssetData>(
        `/v1/accounting/fixed-assets/${id}/place-in-service`,
        input,
      );
      return data;
    },
    onSuccess: () => refreshCapital(qc),
  });
}

export function useRemoveFromService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<FixedAssetData> => {
      await primeCsrf();
      const { data } = await api.post<FixedAssetData>(
        `/v1/accounting/fixed-assets/${id}/remove-from-service`,
      );
      return data;
    },
    onSuccess: () => refreshCapital(qc),
  });
}

export function useDeleteFixedAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/fixed-assets/${id}`);
    },
    onSuccess: () => refreshCapital(qc),
  });
}

export type ManualCostInput = {
  assetId: number;
  component: string;
  amount_cents: number;
  incurred_on: string;
  paid_from_account_id: number;
  memo?: string | null;
  /** Saved with the cost in the same request; a rejected file saves nothing. */
  receipt?: File | null;
};

/**
 * Add a cost paid outside the linked bank accounts (a personal card, cash).
 * Posts DR construction in progress / CR the account that paid.
 */
export function useAddFixedAssetCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assetId,
      memo,
      receipt,
      ...fields
    }: ManualCostInput): Promise<FixedAssetData> => {
      await primeCsrf();
      const form = new FormData();
      for (const [key, value] of Object.entries(fields)) {
        form.append(key, String(value));
      }
      if (memo) form.append('memo', memo);
      if (receipt) form.append('receipt', receipt);
      const { data } = await api.post<FixedAssetData>(
        `/v1/accounting/fixed-assets/${assetId}/costs`,
        form,
      );
      return data;
    },
    onSuccess: () => refreshCapital(qc),
  });
}

/** Remove a hand-entered cost and the entry it posted. Bank-sourced costs unpost from Transactions. */
export function useRemoveFixedAssetCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, costId }: { assetId: number; costId: number }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/fixed-assets/${assetId}/costs/${costId}`);
    },
    onSuccess: () => refreshCapital(qc),
  });
}

/** Attach a receipt or photo to the asset (its `evidence`). */
export function useUploadFixedAssetEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, file }: { assetId: number; file: File }): Promise<void> => {
      await primeCsrf();
      const form = new FormData();
      form.append('file', file);
      await api.post(`/v1/accounting/fixed-assets/${assetId}/evidence`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteFixedAssetEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, mediaId }: { assetId: number; mediaId: number }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/fixed-assets/${assetId}/evidence/${mediaId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export type CapitalizeInput = {
  id: number;
  component: string;
  fixed_asset_id?: number;
  name?: string;
  asset_class?: AssetClass;
};

/** Book a bank charge as part of a capital asset instead of as an expense. */
export function useCapitalizeTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: CapitalizeInput): Promise<void> => {
      await primeCsrf();
      await api.post(`/v1/bank/transactions/${id}/capitalize`, body);
    },
    onSuccess: () => refreshCapital(qc),
  });
}
